import { useState, useEffect } from 'react';
import { fetchUsers, updateUser, deleteUser } from '../services/api';
import { DotsThreeVertical, Pencil, Trash } from "phosphor-react";

const Users = () => {
  const [users, setUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 10;
  const [searchTerm, setSearchTerm] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [userToDelete, setUserToDelete] = useState(null);
  const [formData, setFormData] = useState({ name: '', email: '', balance: '' });
  const [notification, setNotification] = useState({ show: false, message: '', type: 'success' });
  const [isLoading, setIsLoading] = useState({ edit: false, delete: false, initialLoad: true });
  const [errors, setErrors] = useState({ name: '', balance: '' });
  const [openDropdown, setOpenDropdown] = useState(null);

  useEffect(() => {
    const closeDropdown = (e) => {
      if (!e.target.closest('.user-action-dropdown')) {
        setOpenDropdown(null);
      }
    };

    document.addEventListener('click', closeDropdown);
    return () => document.removeEventListener('click', closeDropdown);
  }, []);

  useEffect(() => {
    const loadUsers = async () => {
      try {
        const data = await fetchUsers();
        setUsers(data);
      } catch (error) {
        showNotification('Error loading users: ' + error.message, 'error');
      } finally {
        setIsLoading(prev => ({ ...prev, initialLoad: false }));
      }
    };
    loadUsers();
  }, []);

  const showNotification = (message, type = 'success') => {
    setNotification({ show: true, message, type });
    setTimeout(() => setNotification(prev => ({ ...prev, show: false })), 2000);
  };

  const filteredUsers = users.filter(user =>
    (user.name?.toLowerCase() || '').includes(searchTerm.toLowerCase()) ||
    (user.email?.toLowerCase() || '').includes(searchTerm.toLowerCase())
  );

  const indexOfLastRecord = currentPage * recordsPerPage;
  const paginatedUsers = filteredUsers.slice(indexOfLastRecord - recordsPerPage, indexOfLastRecord);
  const totalPages = Math.ceil(filteredUsers.length / recordsPerPage);

  const handleEditClick = (user) => {
    setCurrentUser(user);
    setFormData({ name: user.name, email: user.email, balance: user.balance.toString() });
    setErrors({ name: '', balance: '' });
    setIsModalOpen(true);
    document.body.classList.add('modal-open');
  };

  const handleUnblockFaceId = async (user) => {
    try{
      const token = localStorage.getItem("adminToken");
      const id = localStorage.getItem("userId");
      const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
      const response = await fetch(`${API_BASE_URL}/smile/unblock/user`, {
        method: 'GET',
        headers: {
          'Accept': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to fetch user');
      }
      showNotification(
        'Face ID unblocked successfully!',
        'success'
      );
      const data = await fetchUsers();
      setUsers(data);
      return await response.json();
    } catch (error) {
      throw error;
    }
  }

  const handleDeleteClick = (user) => {
    setUserToDelete(user);
    setIsDeleteConfirmOpen(true);
    document.body.classList.add('modal-open');
  };

  const closeAllModals = () => {
    setIsModalOpen(false);
    setIsDeleteConfirmOpen(false);
    document.body.classList.remove('modal-open');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const validateName = (name) => {
    if (!name.trim()) return 'Name is required';
    if (name.length < 2) return 'Name must be at least 2 characters';
    if (name.length > 50) return 'Name cannot exceed 50 characters';
    return '';
  };

  const validateBalance = (balance) => {
    if (balance === '') return 'Balance is required';
    if (isNaN(parseFloat(balance))) return 'Must be a valid number';
    if (parseFloat(balance) < 0) return 'Cannot be negative';
    return '';
  };

  const validateForm = () => {
    const nameError = validateName(formData.name);
    const balanceError = validateBalance(formData.balance);
    setErrors({ name: nameError, balance: balanceError });
    return !nameError && !balanceError;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsLoading(prev => ({ ...prev, edit: true }));
    try {
      const updatedData = { name: formData.name, balance: parseFloat(formData.balance) };
      const response = await updateUser(currentUser.id, updatedData);
      setUsers(prev => prev.map(u => u.id === currentUser.id ? { ...u, ...updatedData } : u));
      closeAllModals();
      showNotification(response.message || 'User updated successfully!');
    } catch (error) {
      showNotification('Error updating user: ' + error.message, 'error');
    } finally {
      setIsLoading(prev => ({ ...prev, edit: false }));
    }
  };

  const confirmDelete = async () => {
    setIsLoading(prev => ({ ...prev, delete: true }));
    try {
      await deleteUser(userToDelete.id);
      setUsers(users.filter(u => u.id !== userToDelete.id));
      closeAllModals();
      showNotification('User deleted successfully!');
    } catch (error) {
      showNotification('Error deleting user: ' + error.message, 'error');
    } finally {
      setIsLoading(prev => ({ ...prev, delete: false }));
    }
  };

  return (
    <div className="position-relative">
      {/* Notification */}
      {notification.show && (
        <div className={`alert alert-${notification.type === 'success' ? 'success' : 'danger'} position-fixed top-0 end-0 m-3 shadow`}>
          {notification.message}
        </div>
      )}

      {/* Table */}
      <div className="theme-card-white">
        <div className="card-body">
          <div className="theme-card-header">
            <h5 className="card-head">Users</h5>
            <input
              type="text"
              className="form-control form-control-300 w-auto"
              placeholder="Search Users"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {isLoading.initialLoad ? (
            <div className="d-flex justify-content-center align-items-center py-5">
              <div className="spinner-border text-primary" role="status"></div>
            </div>
          ) : (
            <>
            <div className="table-responsive">
              <table className="table theme-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Contact Number</th>
                    <th>Balance</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedUsers.map((user) => (
                    <tr key={user.id}>
                      <td>{user.id}</td>
                      <td>{user.name}</td>
                      <td>{user.email}</td>
                      <td>{user.contact_number}</td>
                      <td>
                        <span className="badge bg-success text-success">
                          ${parseFloat(user.balance).toFixed(2)}
                        </span>
                      </td>
                      <td>
                        <div className="position-relative user-action-dropdown">
                          <button
                            type="button"
                            className="theme-drop"
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('clicked', user.id); // 🔴 DEBUG LINE
                              setOpenDropdown(prev =>
                                prev === user.id ? null : user.id
                              );
                            }}
                          >
                            <DotsThreeVertical size={18} weight="bold" />
                          </button>

                          {openDropdown === user.id && (
                            <div
                              className="dropdown-menu show"
                              style={{
                                position: 'absolute',
                                right: 0,
                                top: '100%',
                                zIndex: 9999,
                                display: 'block'
                              }}
                            >
                              { user?.is_smile_id_blocked == 1 &&
                                <button
                                  type="button"
                                  className="dropdown-item"
                                  onClick={() => handleUnblockFaceId(user)}
                                >
                                  Unblock face id
                                </button>
                              }

                              <button
                                type="button"
                                className="dropdown-item"
                                onClick={() => handleEditClick(user)}
                              >
                                Edit
                              </button>

                              <button
                                type="button"
                                className="dropdown-item text-danger"
                                onClick={() => handleDeleteClick(user)}
                              >
                                Delete
                              </button>
                            </div>
                          )}
                        </div>
                      </td>

                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
              {/* Pagination */}
              <nav className="d-flex justify-content-end">
                <ul className="pagination mb-0">
                  <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}>
                      Prev
                    </button>
                  </li>
                  {[...Array(totalPages)].map((_, i) => (
                    <li key={i} className={`page-item ${currentPage === i + 1 ? 'active' : ''}`}>
                      <button className="page-link" onClick={() => setCurrentPage(i + 1)}>
                        {i + 1}
                      </button>
                    </li>
                  ))}
                  <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                    <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}>
                      Next
                    </button>
                  </li>
                </ul>
              </nav>
            </>
          )}
        </div>
      </div>

      {/* Edit Modal */}
      {isModalOpen && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Edit User</h5>
                  <button type="button" className="btn-close" onClick={closeAllModals}></button>
                </div>
                <form onSubmit={handleSubmit}>
                  <div className="modal-body">
                    <div className="mb-3">
                      <label className="form-label">Name</label>
                      <input
                        type="text"
                        className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                      />
                      {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Email</label>
                      <input type="email" className="form-control" value={formData.email} disabled />
                    </div>
                    <div className="mb-3">
                      <label className="form-label">Balance</label>
                      <input
                        type="number"
                        className={`form-control ${errors.balance ? 'is-invalid' : ''}`}
                        name="balance"
                        value={formData.balance}
                        onChange={handleInputChange}
                      />
                      {errors.balance && <div className="invalid-feedback">{errors.balance}</div>}
                    </div>
                  </div>
                  <div className="modal-footer">
                    <button type="button" className="border-btn" onClick={closeAllModals}>
                      Cancel
                    </button>
                    <button type="submit" className="theme-btn" disabled={isLoading.edit}>
                      {isLoading.edit ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2"></span>
                          Saving...
                        </>
                      ) : (
                        'Save Changes'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}

      {/* Delete Modal */}
      {isDeleteConfirmOpen && (
        <>
          <div className="modal fade show d-block" tabIndex="-1">
            <div className="modal-dialog modal-dialog-centered">
              <div className="modal-content">
                <div className="modal-header">
                  <h5 className="modal-title">Confirm Delete</h5>
                  <button type="button" className="btn-close" onClick={closeAllModals}></button>
                </div>
                <div className="modal-body">
                  <p className='text-sm font-400 theme-text-black pb-1'>Are you sure you want to delete <strong>{userToDelete?.name}</strong>?</p>
                  <p className="text-muted small mb-0">This action cannot be undone.</p>
                </div>
                <div className="modal-footer">
                  <button className="border-btn" onClick={closeAllModals}>
                    Cancel
                  </button>
                  <button className="theme-btn" onClick={confirmDelete} disabled={isLoading.delete}>
                    {isLoading.delete ? (
                      <>
                        <span className="spinner-border spinner-border-sm me-2"></span>
                        Deleting...
                      </>
                    ) : (
                      'Delete'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
          <div className="modal-backdrop fade show"></div>
        </>
      )}
    </div>
  );
};

export default Users;